/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Stack } from '@mui/material';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import { CssTextField } from 'pages/reference-implementation/components/select';
import { InputLabel } from 'pages/reference-implementation/components/input-label';

const SqlServiceAccountName = ({ value, environment, onChange, onValidationChange, isRequired }) => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [isSqlAccNameValid, setSqlAccNameValid] = React.useState(false);
    const [hasValidated, setHasValidated] = React.useState(false);
    const hasInitializedValidation = React.useRef(false);
    const sqlServiceAccountNameApi = useMutation((payload) => axios.post(`${endPoints?.ssp?.sqlPaas?.sqlserviceAccountNameApi}`, payload));

    React.useEffect(() => {
        const computedValidity = !!value && isSqlAccNameValid;
        onValidationChange?.(computedValidity);
    }, [value, isSqlAccNameValid, onValidationChange]);

    React.useEffect(() => {
        if (sqlServiceAccountNameApi?.data?.data?.serviceAccountExist === false) {
            setSqlAccNameValid(false);
        } else if (sqlServiceAccountNameApi?.data?.data?.serviceAccountExist === true) {
            setSqlAccNameValid(true);
        }
    }, [sqlServiceAccountNameApi?.data?.data]);

    const validateSqlAccount = React.useCallback(() => {
        if (value) {
            setHasValidated(true);
            sqlServiceAccountNameApi.mutate({
                accountType: 'Active Directory',
                serviceAccountId: value,
                environment,
            });
            return;
        }
        setHasValidated(true);
        setSqlAccNameValid(false);
    }, [environment, sqlServiceAccountNameApi, value]);
    React.useEffect(() => {
        if (hasInitializedValidation.current) {
            return;
        }

        hasInitializedValidation.current = true;

        if (!value) {
            setHasValidated(false);
            setSqlAccNameValid(false);
            onValidationChange?.(false);
            return;
        }

        setHasValidated(false);
        setSqlAccNameValid(false);
        validateSqlAccount();
    }, [validateSqlAccount, value, onValidationChange]);

    React.useEffect(() => {
        if (!value && isRequired) setIsTouched(true);
    }, [value, isRequired]);

    const validateInput = () => {
        if (isTouched && !value && isRequired) return 'validate';
        if (value) {
            return isRequired && !isSqlAccNameValid && value !== '' && value !== null && value !== undefined ? 'validate' : '';
        }
        return '';
    };

    const showRequiredError = isRequired && !value;
    const showLookupError = hasValidated && !!value && !isSqlAccNameValid;
    let helperText = '';
    if (showLookupError) {
        helperText = 'Given Service Account does not exist.';
    }

    return (
        <Stack direction="row" spacing={1}>
            <CssTextField
                size="small"
                label={<InputLabel label="Active Directory SQL Account" isRequired />}
                autoComplete="off"
                fullWidth
                error={showRequiredError || showLookupError}
                helperText={helperText}
                onChange={(event) => onChange(event.target.value)}
                onBlur={() => validateSqlAccount()}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        validateSqlAccount();
                    }
                }}
                value={value || ''}
                className={validateInput()}
            />
        </Stack>
    );
};

SqlServiceAccountName.propTypes = {
    value: PropTypes.string,
    environment: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onValidationChange: PropTypes.func,
    isRequired: PropTypes.bool,
};

SqlServiceAccountName.defaultProps = {
    value: '',
    environment: '',
    onValidationChange: () => {},
    isRequired: false,
};

export default React.memo(SqlServiceAccountName);
